import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { StatBlock, TableBlock, ChartBlock } from "./MessageBlocks";

const tryParse = (raw) => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

// Shown while an embedded viz fence is still streaming in (JSON not yet complete).
const VizPlaceholder = ({ type }) => (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-4 text-xs text-muted-foreground">
        <span className="h-2 w-2 rounded-full bg-primary/60 animate-pulse" />
        Rendering {type}…
    </div>
);

const renderViz = (lang, raw) => {
    const parsed = tryParse(raw.trim());
    if (!parsed) return <VizPlaceholder type={lang} />;
    if (lang === "stat") {
        const items = Array.isArray(parsed) ? parsed : parsed.items || [];
        return <StatBlock items={items} />;
    }
    if (lang === "table") return <TableBlock columns={parsed.columns} rows={parsed.rows} caption={parsed.caption} />;
    if (lang === "chart")
        return <ChartBlock chartType={parsed.chartType} title={parsed.title} data={parsed.data} series={parsed.series} />;
    return null;
};

const components = {
    // Let the `code` renderer own block layout (so viz fences aren't wrapped in <pre>).
    pre: ({ children }) => <div className="my-2 space-y-2">{children}</div>,
    code: ({ className, children, ...props }) => {
        const lang = /language-(\w+)/.exec(className || "")?.[1];
        const raw = String(children ?? "");
        if (lang === "stat" || lang === "table" || lang === "chart") {
            return renderViz(lang, raw);
        }
        if (!className) {
            return (
                <code className="px-1 py-0.5 rounded bg-muted text-[0.85em]" {...props}>
                    {children}
                </code>
            );
        }
        return (
            <pre className="rounded-md bg-muted p-2 overflow-x-auto text-xs">
                <code {...props}>{children}</code>
            </pre>
        );
    },
};

// Renders streaming assistant markdown, with embedded stat/table/chart fences
// rendered as live components. Styling targets child elements so GFM tables,
// lists and links look right without the Tailwind typography plugin.
function MarkdownMessage({ text = "", streaming = false }) {
    return (
        <div
            className="text-sm leading-relaxed space-y-2 break-words
        [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5
        [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold
        [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold
        [&_table]:w-full [&_table]:border [&_table]:my-1 [&_thead]:bg-muted/50
        [&_th]:text-left [&_th]:font-medium [&_th]:p-1.5 [&_th]:border [&_td]:p-1.5 [&_td]:border [&_td]:text-xs"
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {text}
            </ReactMarkdown>
            {streaming && text && (
                <span className="inline-block h-3.5 w-1.5 align-text-bottom bg-primary/70 animate-pulse" />
            )}
        </div>
    );
}

export default memo(MarkdownMessage);
